import type { ReactElement } from "react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ApiError } from "@/api/client";
import { useDraftChanges, useRepoDrafts, useRepoVersions } from "@/api/queries/repoLifecycle";
import { FreischaltenNachFreigabeDialog } from "@/components/plugin-catalog/FreischaltenNachFreigabeDialog";
import {
  useApproveDraft,
  useCreateDraft,
  useDiscardDraft,
  useSubmitDraft,
} from "@/api/mutations/repoLifecycle";
import type { ManagedRepo } from "@/types";
import {
  LIVE_REF,
  draftStateLabel,
  draftStateVariant,
  isLive,
  lifecycleErrorMessage,
  refLabel,
  slugify,
} from "./lifecycleVocab";

// #278: die Bedienung des Lifecycle — in Fachsprache. Entwurf anlegen, bearbeiten,
// einreichen, freigeben. Git-Vokabular kommt hier nicht vor.

function errMsg(err: unknown, fallback: string): string {
  const code = err instanceof ApiError ? (err.body as { code?: string } | undefined)?.code : undefined;
  return lifecycleErrorMessage(code, err instanceof Error ? err.message : fallback);
}

export function LifecycleBar({
  repo,
  activeRef,
  onRefChange,
}: {
  repo: ManagedRepo;
  activeRef: string;
  onRefChange: (ref: string) => void;
}): ReactElement | null {
  const drafts = useRepoDrafts(repo.id);
  const versions = useRepoVersions(repo.id);
  const changes = useDraftChanges(repo.id, isLive(activeRef) ? null : activeRef);
  const createDraft = useCreateDraft();
  const discardDraft = useDiscardDraft();
  const submitDraft = useSubmitDraft();
  const approveDraft = useApproveDraft();

  const [newOpen, setNewOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitTitle, setSubmitTitle] = useState("");
  const [submitDesc, setSubmitDesc] = useState("");
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveTag, setApproveTag] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [needsOverride, setNeedsOverride] = useState(false);
  // #288 (UC-6): Verkettung nach der Freigabe eines KATALOG-Entwurfs — rein Bedien-Ablauf,
  // der Server koppelt die beiden Entscheidungen bewusst nicht.
  const [freischaltenOpen, setFreischaltenOpen] = useState(false);

  // #275 (E-4): Repos ohne Freigabe-Pflicht bekommen KEINE Lifecycle-Bedienung — sonst
  // zeigte die UI Knöpfe, die im Backend gar nicht greifen (dort ist der Live-Write erlaubt).
  if (repo.lifecycle === "direct") return null;

  const list = drafts.data ?? [];
  const current = list.find((d) => d.branch === activeRef) ?? null;
  const currentVersion = (versions.data ?? []).find((v) => v.isCurrent) ?? null;

  const onCreate = async (): Promise<void> => {
    const slug = slugify(newTitle);
    if (!slug) {
      toast.error("Bitte einen Namen aus Buchstaben oder Ziffern angeben.");
      return;
    }
    try {
      const d = await createDraft.mutateAsync({ repoId: repo.id, slug });
      setNewOpen(false);
      setNewTitle("");
      onRefChange(d.branch); // direkt in den neuen Entwurf springen
      toast.success(`Entwurf „${d.title}" angelegt.`);
    } catch (err) {
      toast.error(errMsg(err, "Entwurf konnte nicht angelegt werden."));
    }
  };

  const onDiscard = async (): Promise<void> => {
    if (!current) return;
    try {
      await discardDraft.mutateAsync({ repoId: repo.id, branch: current.branch });
      onRefChange(LIVE_REF);
      toast.success("Entwurf verworfen.");
    } catch (err) {
      toast.error(errMsg(err, "Entwurf konnte nicht verworfen werden."));
    }
  };

  const onSubmit = async (): Promise<void> => {
    if (!current) return;
    try {
      await submitDraft.mutateAsync({
        repoId: repo.id,
        branch: current.branch,
        title: submitTitle || current.title,
        description: submitDesc || undefined,
      });
      setSubmitOpen(false);
      toast.success("Zur Freigabe eingereicht.");
    } catch (err) {
      toast.error(errMsg(err, "Einreichen fehlgeschlagen."));
    }
  };

  const onApprove = async (): Promise<void> => {
    if (!current) return;
    try {
      const res = await approveDraft.mutateAsync({
        repoId: repo.id,
        branch: current.branch,
        tag: approveTag.trim() || undefined,
        ...(needsOverride ? { override: true, overrideReason: overrideReason.trim() } : {}),
      });
      setApproveOpen(false);
      setNeedsOverride(false);
      setOverrideReason("");
      setApproveTag("");
      onRefChange(LIVE_REF);
      // Ehrlich statt fake-atomar: der Merge ist passiert, auch wenn die Version scheiterte.
      if (res.tagError) {
        toast.warning(`Freigegeben — die Version konnte nicht gesetzt werden (${res.tagError}).`);
      } else {
        toast.success(res.overridden ? "Freigegeben (Vier-Augen übergangen)." : "Freigegeben — jetzt live.");
      }
      // Nur bei Marktplätzen — nur dort gibt es Plugins zum Freischalten. Der Dialog
      // schließt sich von selbst, wenn nichts offen ist (z.B. reine Textkorrektur).
      if (repo.kind === "marketplace") setFreischaltenOpen(true);
    } catch (err) {
      const code = err instanceof ApiError ? (err.body as { code?: string } | undefined)?.code : undefined;
      if (code === "FOUR_EYES_REQUIRED") {
        // Kein generischer Fehler: erklären und den begründeten Weg anbieten.
        setNeedsOverride(true);
        toast.warning(lifecycleErrorMessage(code, ""));
        return;
      }
      toast.error(errMsg(err, "Freigabe fehlgeschlagen."));
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2" data-testid="lifecycle-bar">
      <span className="text-[12px] text-text-muted">Stand:</span>
      <select
        className="h-8 rounded-md border border-border bg-surface px-2 text-[13px]"
        value={activeRef}
        onChange={(e) => onRefChange(e.target.value)}
        data-testid="ref-switcher"
      >
        <option value={LIVE_REF}>{refLabel(LIVE_REF, list)}</option>
        {list.map((d) => (
          <option key={d.branch} value={d.branch}>
            {d.title} — {draftStateLabel(d.state)}
          </option>
        ))}
        {/* Der aktive Stand kommt aus der Adresszeile und ist da, BEVOR die Entwurfs-Liste
            geladen ist (geteilter Link, Neuladen). Ohne diese Option hätte das <select>
            keinen passenden Eintrag und fiele sichtbar auf „Live" zurück — die Leiste
            behauptete „Live", während der Inhalt der Entwurf ist. */}
        {!isLive(activeRef) && !list.some((d) => d.branch === activeRef) && (
          <option value={activeRef}>{drafts.isLoading ? "wird geladen …" : refLabel(activeRef, list)}</option>
        )}
      </select>

      {current && (
        <Badge variant={draftStateVariant(current.state)} data-testid="draft-state">
          {draftStateLabel(current.state)}
        </Badge>
      )}
      {isLive(activeRef) && currentVersion && (
        // #292 (UC-5): das ist die REPO-Version (bei der Freigabe vergebener Tag) —
        // nicht die Manifest-Version, die der Autor im Katalog-Eintrag pflegt.
        <Badge
          variant="outline"
          data-testid="current-version"
          title="Repo-Version: bei einer Freigabe vergebener Tag. Die Manifest-Version des Plugins ist die Autor-Angabe im Katalog-Eintrag."
        >
          Repo-Version {currentVersion.tag}
        </Badge>
      )}
      {current?.createdBy && (
        <span className="text-[12px] text-text-muted">zuletzt bearbeitet von {current.createdBy}</span>
      )}

      <div className="ml-auto flex items-center gap-2">
        {isLive(activeRef) ? (
          <Button size="sm" variant="outline" onClick={() => setNewOpen(true)} data-testid="new-draft">
            Entwurf anlegen
          </Button>
        ) : (
          <>
            {changes.data && (
              <span className="text-[12px] text-text-muted" data-testid="change-count">
                {changes.data.files.length} geänderte Datei(en)
              </span>
            )}
            {changes.data && !changes.data.schemaOk && (
              <Badge variant="destructive" data-testid="schema-invalid">
                Ungültige Datei
              </Badge>
            )}
            {current?.state === "draft" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setSubmitTitle(current.title);
                  setSubmitOpen(true);
                }}
                data-testid="submit-draft"
              >
                Zur Freigabe einreichen
              </Button>
            )}
            {current?.state === "submitted" && current.canApprove && (
              <Button size="sm" onClick={() => setApproveOpen(true)} data-testid="approve-draft">
                Freigeben
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => void onDiscard()} data-testid="discard-draft">
              Verwerfen
            </Button>
          </>
        )}
      </div>

      {/* Entwurf anlegen */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Entwurf anlegen</DialogTitle>
            <DialogDescription>
              Änderungen werden zunächst im Entwurf gesammelt. Live wird erst, was freigegeben wird.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="draft-title">Name</Label>
            <Input
              id="draft-title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="z.B. Neuer Katalog-Eintrag"
              data-testid="draft-title-input"
            />
            {newTitle && !slugify(newTitle) && (
              <p className="text-[12px] text-danger">Bitte Buchstaben oder Ziffern verwenden.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={() => void onCreate()} disabled={createDraft.isPending} data-testid="draft-create-confirm">
              Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Einreichen */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Zur Freigabe einreichen</DialogTitle>
            <DialogDescription>
              Der Entwurf wird zur Entscheidung gestellt. Live ist er damit noch nicht.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="submit-title">Titel</Label>
              <Input id="submit-title" value={submitTitle} onChange={(e) => setSubmitTitle(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="submit-desc">Was wurde geändert? (optional)</Label>
              <Textarea id="submit-desc" rows={3} value={submitDesc} onChange={(e) => setSubmitDesc(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSubmitOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={() => void onSubmit()} disabled={submitDraft.isPending} data-testid="submit-confirm">
              Einreichen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Freigeben */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Freigeben</DialogTitle>
            <DialogDescription>
              Der Entwurf wird zum Live-Stand. Geänderte Manifest-Dateien werden vorher geprüft.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {changes.data && !changes.data.schemaOk && (
              <div className="rounded-md border border-danger-border bg-danger-bg p-3 text-[13px]">
                <p className="font-medium">Diese Änderung ist nicht gültig:</p>
                <ul className="mt-1 list-disc pl-5">
                  {changes.data.schemaIssues.map((i, n) => (
                    <li key={n}>
                      {i.path ? `${i.path}: ` : ""}
                      {i.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="approve-tag">Version vergeben (optional)</Label>
              <Input
                id="approve-tag"
                value={approveTag}
                onChange={(e) => setApproveTag(e.target.value)}
                placeholder="z.B. v1.2.0"
                data-testid="approve-tag"
              />
            </div>
            {needsOverride && (
              <div className="space-y-2 rounded-md border border-warn-border bg-warn-bg p-3">
                <p className="text-[13px]">
                  Diesen Entwurf hat zuletzt dieselbe Person bearbeitet. Das Übergehen wird gesondert
                  protokolliert.
                </p>
                <Label htmlFor="override-reason">Begründung (Pflicht)</Label>
                <Textarea
                  id="override-reason"
                  rows={2}
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  data-testid="override-reason"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproveOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => void onApprove()}
              disabled={approveDraft.isPending || (needsOverride && overrideReason.trim().length === 0)}
              data-testid="approve-confirm"
            >
              {needsOverride ? "Trotzdem freigeben" : "Freigeben"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* #288 (UC-6): Verkettung — nach der Freigabe eines Katalog-Entwurfs anbieten, die
          enthaltenen Plugins gleich freizuschalten. Zwei Entscheidungen, ein Ablauf. */}
      {freischaltenOpen && (
        <FreischaltenNachFreigabeDialog
          // #291 (Review-Fund 12): die Server-Zusage nutzen — der Skew-Fallback bleibt
          // nur für einen älteren Master.
          marketplaceId={repo.marketplaceId ?? repo.id.replace(/^mp-/, "")}
          open
          onOpenChange={setFreischaltenOpen}
        />
      )}
    </div>
  );
}
